import React, { useEffect, useContext, useState } from "react";
import "../ContentPages/style.css";
import SubmitButton from "../Buttons/SubmitButton";
import BackButton from "../Buttons/BackButton";
import { useForm } from "react-hook-form";
import FieldError from "../FieldError";
import { AppContext } from "../../contexts/AppContext";
import { getCountries } from "../../Services/metadataService";
import Loader from "../Loader/Loader.jsx";
import {
  initiateApplication,
  updateApplication,
} from "../../Services/applicationService"; // import the save function
import { toTitleCase } from "../../Utils/stringHandler.tsx"; // Assuming you have a utility function for title case
import { calculateAge } from "../../Services/localStorage.tsx";
import { redirectToRealMe } from "../../Services/apiService.jsx";
import AgeWarningModal from "../Popups/AgeWarningModal/index.jsx";
import FloatingTextField from "../FormFields/FloatingTextField.tsx";
import FloatingDateField from "../FormFields/FloatingDateField.tsx";
import Calendar from "../../assets/images/Calendar.svg";
import FloatingSelectField from "../FormFields/FloatingSelectField.tsx";

export interface BirthCertificateFormData {
  givenName?: string; // Optional
  surname: string;
  dateOfBirth: string;
  placeOfBirth: string;
  country: string;
  gender: string;
  motherGivenName?: string; // Optional
  motherFamily: string;
  fatherGivenName?: string; // Optional
  fatherFamily?: string; // Optional, but you had `required:` rule — adjust if truly required
  applicationId?: string; // This is dynamically added before saving
}

interface Country {
  dia_countryid: string;
  Name: string;
  Statecode: number;
  "ISO code": string;
}

const BirthCertificate = ({ handleNext, handleBack }) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
    watch,
    trigger,
  } = useForm<BirthCertificateFormData>({
    defaultValues: {
      country: "18ebb77f-2341-e911-a85e-000d3ae0eb6e",
    },
    shouldFocusError: true,
    mode: "onTouched",
  });
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [pendingData, setPendingData] = useState({});

  const { appState, setAppState, userFlows, setUserFlows } =
    useContext(AppContext);

  const gender = watch("gender") || "";

  useEffect(() => {
    const initializeForm = async () => {
      const applicant = appState?.requestPayload?.applicant;
      const event = appState?.requestPayload?.eventTypeSearch?.[0];
      const supporting = appState?.requestPayload?.supportingInformation;

      getCountries()
        .then((meta) => {
          const parsed = JSON.parse(meta.countries || "[]") as Country[];

          let filtered = parsed.filter((c) => c.Statecode === 0);

          let ordered = [
            ...filtered.filter((c) => c.Name === "New Zealand"),
            ...filtered.filter((c) => c.Name !== "New Zealand"),
          ];

          let unique: Country[] = Array.from(
            new Map(ordered.map((c) => [c.dia_countryid, c])).values()
          );

          setCountries(unique);
          if (applicant || event || supporting) {
            reset({
              givenName: toTitleCase(applicant?.givenNames) || "",
              surname: toTitleCase(applicant?.familyName) || "",
              dateOfBirth: applicant?.dateOfBirth || "",
              placeOfBirth: toTitleCase(applicant?.placeOfBirth || ""),
              country:
                event?.countryOfBirthId ||
                "18ebb77f-2341-e911-a85e-000d3ae0eb6e",
              gender: applicant?.genderId,
              motherGivenName: toTitleCase(supporting?.parent1GivenNames) || "",
              motherFamily: toTitleCase(supporting?.parent1FamilyName) || "",
              fatherGivenName: toTitleCase(supporting?.parent2GivenNames) || "",
              fatherFamily: toTitleCase(supporting?.parent2FamilyName) || "",
            });
          }
        })
        .catch((err) => console.error("Failed to load country list", err));
    };
    initializeForm();
  }, [appState, reset]);

  // const calculateAge = (dob) => {
  //   const birth = new Date(dob);
  //   const now = new Date();
  //   let age = now.getFullYear() - birth.getFullYear();
  //   const m = now.getMonth() - birth.getMonth();
  //   if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  //   return age;
  // };

  const onSubmit = (data: BirthCertificateFormData) => {
    const age = calculateAge(data.dateOfBirth);
    if (age < 14) {
      setPendingData(data);
      setShowAgeModal(true);
      setUserFlows({
        ...userFlows,
        showLiveness: false,
        childApplication: true,
      });
      return;
      // saveApplication(data, "fee0075c-6dcd-ef11-b8e9-00224891c497");
    } else {
      saveApplication(data, "dee0075c-6dcd-ef11-b8e9-00224891c497");
    }
  };

  const handleAgeModalCancel = () => {
    redirectToRealMe();
  };
  const handleAgeModalContinue = () => {
    setShowAgeModal(false);
    if (pendingData) {
      saveApplication(pendingData, "fee0075c-6dcd-ef11-b8e9-00224891c497");
    }
  };

  const saveApplication = (
    data: BirthCertificateFormData,
    applicationTypeId: string
  ) => {
    setLoading(true);
    setUserFlows((prev: any) => ({
      ...prev,
      showRefree: prev.isRenew ? false : true,
    }));

    const updatedAppState = {
      ...appState,
      requestPayload: {
        ...appState.requestPayload,
        applicationTypeId: applicationTypeId,
        applicationFlowCode: 100000000,
        applicant: {
          ...appState.requestPayload.applicant,
          givenNames: toTitleCase(data.givenName || ""),
          familyName: toTitleCase(data.surname),
          dateOfBirth: data.dateOfBirth,
          placeOfBirth: toTitleCase(data.placeOfBirth),
          countryOfBirthId: data.country,
          genderId: data.gender,
          namesKnownAs: [
            {
              nameTypeId: "3ecbc107-c3b8-e911-a866-000d3ae10c61",
              familyName: toTitleCase(data.surname),
              givenNames: toTitleCase(data.givenName || ""),
            },
          ],
        },
        supportingInformation: {
          parent1GivenNames: toTitleCase(data.motherGivenName || ""),
          parent1FamilyName: toTitleCase(data.motherFamily),
          parent2GivenNames: toTitleCase(data.fatherGivenName || ""),
          parent2FamilyName: toTitleCase(data.fatherFamily || ""),
        },
        eventTypeSearch: [
          {
            eventTypeId: "700a9fae-a634-ed11-9db1-0022489412bc",
            givenNames: toTitleCase(data.givenName || "").slice(0, 100),
            familyName: toTitleCase(data.surname).slice(0, 100),
            dateOfBirth: toTitleCase(data.dateOfBirth),
            countryOfBirthId: data.country,
            genderId: data.gender,
          },
        ],
      },
    };
     // console.log("Updated Application State:", updatedAppState);
    if (updatedAppState.requestPayload.applicationId) {
      updateApplication(updatedAppState.requestPayload)
        .then((response) => {
          console.log("Application updated successfully:", response);
          setAppState(updatedAppState);
          setLoading(false);
          handleNext();
        })
        .catch((error) => {
          console.error("Error updating application:", error);
          setLoading(false);
        });
    } else
      initiateApplication(updatedAppState.requestPayload)
        .then((response) => {
          console.log("Application initiated successfully:", response);
          updatedAppState.requestPayload.applicationId = response.serviceId;
          setAppState(updatedAppState);
          setLoading(false);
          handleNext();
        })
        .catch((error) => {
          console.error("Error initiating application:", error);
          setLoading(false);
        });
  };

  const onClose = () => {
    setShowAgeModal(false);
  };

  return (
    <>
      {loading && <Loader />}
      {showAgeModal && (
        <AgeWarningModal
          onContinue={handleAgeModalContinue}
          onCancel={handleAgeModalCancel}
          onClose={onClose}
          extraLine="In some cases, you may also need an identity referee to confirm your details."
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="intro">
          <h1 className="intro-title-mi" lang="mi-NZ">
            Kōrero mai ko wai koe
          </h1>
          <hr />
          <h1 className="intro-title">Tell us who you are</h1>

          <div className="step-container">
            <h2 className="step-title mb-2">
              Your New Zealand birth certificate details
            </h2>
            <p className="mb-4">
              Enter the details exactly as they appear on your birth
              certificate.
            </p>

            <FloatingTextField
              name="givenName"
              label="Your given name(s)"
              inputProps={register("givenName", {
                validate: (value) => {
                  return (
                    !value ||
                    value?.trim().length < 198 ||
                    "Given name(s) must be less than 198 characters."
                  );
                },
              })}
              error={errors.givenName}
            />
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              If you have one name, enter it as your surname or family name.
            </p>

            <FloatingTextField
              name="surname"
              label="Your surname or family name"
              required
              inputProps={register("surname", {
                required:
                  "If you are entering one name, please enter it in surname or family name.",
                maxLength: {
                  value: 84,
                  message: "Family name must be less than 85 characters.",
                },
              })}
              error={errors.surname}
            />
            <div style={{ marginTop: 40 }} />
            <FloatingDateField
              name="dateOfBirth"
              label="Date of birth (dd/mm/yyyy)"
              control={control}
              calendarIcon={Calendar}
              error={errors.dateOfBirth}
              required
            />

            <FloatingTextField
              name="placeOfBirth"
              label="Place of birth (town/city)"
              required
              inputProps={register("placeOfBirth", {
                required: "This field is required",
                maxLength: {
                  value: 100,
                  message:
                    "Place of birth must be less than or equal to 100 characters.",
                },
              })}
              error={errors.placeOfBirth}
            />

            <FloatingSelectField
              name="country"
              label="Country of birth"
              required
              register={register}
              options={countries.map((c) => ({
                value: c.dia_countryid,
                label: c.Name,
              }))}
              value={watch("country")}
              error={errors.country}
            />
            <div style={{ marginTop: 40 }} />

            <FloatingSelectField
              name="gender"
              label="Gender"
              required
              register={register}
              options={[
                {
                  value: "dcd24bf2-7a1f-e911-a849-000d3ae0b82e",
                  label: "Male",
                },
                {
                  value: "c77f9cf8-7a1f-e911-a849-000d3ae0b82e",
                  label: "Female",
                },
                {
                  value: "353656ca-3820-e911-a853-000d3ae0a6cf",
                  label: "Non-binary",
                },
              ]}
              value={watch("gender")}
              error={errors.gender}
            />
            {gender === "353656ca-3820-e911-a853-000d3ae0a6cf" ? (
              <FieldError
                error={null}
                info={
                  "Other agencies that use RealMe may record your gender as Other, due to system conditions."
                }
              />
            ) : (
              ""
            )}
            <div style={{ marginTop: 40 }}></div>

            <FloatingTextField
              name="motherGivenName"
              label="Parent 1/Mother's given name(s)"
              inputProps={register("motherGivenName", {
                validate: (value) => {
                  return (
                    !value ||
                    value?.trim().length < 198 ||
                    "Parent 1/Mother's given name(s) must be less than 198 characters."
                  );
                },
              })}
              error={errors.motherGivenName}
            />

            <FloatingTextField
              name="motherFamily"
              label="Parent 1/Mother's surname or family name"
              required
              inputProps={register("motherFamily", {
                required:
                  "If you are entering one name, please enter it in surname or family name.",
                validate: (value, { motherGivenName }) => {
                  return (
                    !value ||
                    value?.trim().length < 85 ||
                    "Parent 1/Mother's surname or family name must be less than 85 characters."
                  );
                },
              })}
              error={errors.motherFamily}
            />
            <div style={{ marginTop: 40 }}></div>
            <FloatingTextField
              name="fatherGivenName"
              label="Parent 2/Father's given name(s)"
              inputProps={register("fatherGivenName", {
                validate: (value) => {
                  return (
                    !value ||
                    value?.trim().length < 198 ||
                    "Parent 2/Father's given name(s) must be less than 198 characters."
                  );
                },
                onBlur: (e) => {
                  // Removing cross-field validation to avoid confusing UX ticket number: 212422
                  // Trigger validation of fatherFamily when fatherGivenName changes
                  // register("fatherGivenName").onChange(e);
                  // trigger("fatherFamily");
                },
              })}
              error={errors.fatherGivenName}
            />

            <FloatingTextField
              name="fatherFamily"
              label="Parent 2/Father's surname or family name"
              inputProps={register("fatherFamily", {
                validate: (value, { fatherGivenName }) => {
                  // Removing validation as said in ticket number: 212422
                  // if (fatherGivenName && !value) {
                  //   return "If you are entering one name, please enter it in surname or family name.";
                  // }

                  return (
                    !value ||
                    value?.trim().length < 85 ||
                    "Parent 2/Father's surname or family name must be less than 85 characters."
                  );
                },
              })}
              error={errors.fatherFamily}
            />
          </div>
        </div>

        <div className="step-button-container">
          <SubmitButton
            onClick={() => {}}
            text={"Save and continue"}
            type="submit"
            disabled={!isValid}
          />
          <BackButton text={"Back"} onClick={handleBack} />
        </div>
      </form>
    </>
  );
};

export default BirthCertificate;
